import type React from 'react'
import type { HTMLInputTypeAttribute, JSX, KeyboardEvent, Ref } from 'react'
import type { IActionButtonProps } from 'atoms/ActionButton'
import type { Colors } from 'atoms/TextBox/TextBox.h'
import type {
  InputActionButtonIndent,
  InputActionButtonIndentAdaptive,
  InputHeight,
  InputMargin,
  InputNames,
  InputPadding,
  InputPaddingsAdaptive,
} from './Input.h'

import { useCallback, useRef, useState } from 'react'
import { ActionButton, ActionButtonColors } from 'atoms/ActionButton'
import { ButtonFonts, ButtonIconSizes } from 'atoms/Button'
import { IconNames } from 'atoms/Icon'
import { TextBox, TextBoxColors, TextBoxTypes } from 'atoms/TextBox'
import { FIXED_INDENTS } from 'vars/fixed-indents'
import {
  InputActionButtonPosition,
  InputBackgroundColors,
  InputBorderColors,
  InputBorders,
  InputCaretColors,
  InputCorners,
  InputFontColors,
  InputFonts,
  InputPaddings,
} from './Input.h'
import { InputLabel, InputSC, InputWrapper } from './Input.styled'

export interface IInputProps {
  ref?: Ref<HTMLInputElement>
  children?: React.ReactNode
  id?: string
  type?: HTMLInputTypeAttribute
  name?: InputNames
  pattern?: string
  value?: string
  defaultValue?: string
  min?: string
  max?: string
  minLength?: number
  maxLength?: number
  placeholder?: string
  placeholderColor?: InputFontColors
  font?: InputFonts | TextBoxTypes
  colorDefault?: InputFontColors
  colorDisabled?: InputFontColors
  label?: string
  labelFont?: TextBoxTypes
  labelColorDefault?: TextBoxColors | Colors
  labelGap?: FIXED_INDENTS
  inputLabelWidth?: string
  inputWrapperWidth?: string
  inputWrapperHeight?: string
  height?: InputHeight
  icon?: IconNames
  iconSize?: ButtonIconSizes
  iconFont?: ButtonFonts
  paddings?: InputPadding | InputPaddingsAdaptive
  inputLabelPaddings?: InputPadding
  inputLabelMargins?: InputMargin
  corners?: InputCorners
  required?: boolean
  checked?: boolean
  defaultChecked?: boolean
  secured?: boolean
  disabled?: boolean
  invalid?: boolean
  borders?: InputBorders
  borderColorDefault?: InputBorderColors
  borderColorHovered?: InputBorderColors
  borderColorPressed?: InputBorderColors
  borderColorInvalid?: InputBorderColors
  backgroundColorDefault?: InputBackgroundColors
  backgroundColorHovered?: InputBackgroundColors
  backgroundColorPressed?: InputBackgroundColors
  actionButtonColorDefault?: ActionButtonColors
  actionButtonColorHovered?: ActionButtonColors
  actionButtonColorPressed?: ActionButtonColors
  actionButtonPaddings?: IActionButtonProps['paddings']
  actionButtonPosition?: InputActionButtonPosition
  actionButtonIndent?: InputActionButtonIndent | InputActionButtonIndentAdaptive
  caretColor?: InputCaretColors
  autoComplete?: string
  autoFocus?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
  onClick?: IActionButtonProps['onClick']
  voiceSearch?: boolean
  onVoiceSearch?: () => void
}

Input.displayName = 'Input'
export function Input({
  ref: innerRef,
  children,
  id,
  type = 'text',
  name,
  pattern,
  value,
  defaultValue,
  min,
  max,
  minLength,
  maxLength,
  placeholder = '',
  placeholderColor = InputFontColors.TEXT_ICON_PRIMARY,
  font = InputFonts.Body3,
  colorDefault = InputFontColors.TEXT_ICON_PRIMARY,
  colorDisabled = InputFontColors.TEXT_ICON_SECONDARY,
  label = '',
  labelFont = TextBoxTypes.Caption3,
  labelColorDefault = TextBoxColors.TEXT_ICON_SECONDARY,
  labelGap = FIXED_INDENTS.INDENT_0,
  inputLabelWidth,
  inputWrapperWidth,
  inputWrapperHeight,
  height,
  icon,
  iconSize = ButtonIconSizes.small,
  paddings = InputPaddings.SIZE_0,
  inputLabelPaddings,
  corners = InputCorners.CORNERS_10,
  required = false,
  checked,
  defaultChecked,
  secured,
  disabled,
  invalid,
  borders = InputBorders.BORDER_0,
  borderColorDefault = InputBorderColors.SURFACE_OPACITY_LIGHT_10,
  borderColorHovered = InputBorderColors.SURFACE_OPACITY_LIGHT_15,
  borderColorPressed = InputBorderColors.SURFACE_OPACITY_LIGHT_20,
  borderColorInvalid = InputBorderColors.ADDITIONAL_RED_2,
  backgroundColorDefault = InputBackgroundColors.SURFACE_OPACITY_LIGHT_10,
  backgroundColorHovered = InputBackgroundColors.SURFACE_OPACITY_LIGHT_15,
  backgroundColorPressed = InputBackgroundColors.SURFACE_OPACITY_LIGHT_20,
  actionButtonColorDefault = ActionButtonColors.COLOR_MAIN_DEFAULT,
  actionButtonColorHovered = ActionButtonColors.SURFACE_LIGHT_2,
  actionButtonColorPressed = ActionButtonColors.COLOR_MAIN_PRESSED,
  actionButtonPaddings,
  actionButtonPosition = InputActionButtonPosition.Right,
  actionButtonIndent,
  caretColor = InputCaretColors.TEXT_ICON_SECONDARY,
  onChange,
  onBlur,
  onKeyDown,
  autoComplete,
  autoFocus,
  onClick,
}: IInputProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isSecured, setSecured] = useState(secured)

  const handleClick = useCallback((e) => {
    // Used for input with password
    if (secured) {
      setSecured((prop) => !prop)
    }
    onClick?.(e)
  }, [])

  const iconName =
    icon ??
    (typeof isSecured === 'boolean'
      ? isSecured
        ? IconNames.eyeCrossed
        : IconNames.eyeOpen
      : undefined)

  return (
    <InputLabel
      inputLabelWidth={inputLabelWidth}
      backgroundColorPressed={backgroundColorPressed}
      borderColorPressed={borderColorPressed}
      inputLabelPaddings={inputLabelPaddings}
      labelGap={labelGap}
    >
      <TextBox title={label} font={labelFont} color={labelColorDefault} />
      <InputWrapper
        inputWrapperWidth={inputWrapperWidth}
        inputWrapperHeight={inputWrapperHeight}
        actionButtonPosition={actionButtonPosition}
        actionButtonIndent={actionButtonIndent}
      >
        <InputSC
          ref={innerRef || inputRef}
          id={id}
          type={isSecured ? 'password' : type}
          name={name}
          pattern={pattern}
          value={value}
          defaultValue={defaultValue}
          min={min}
          max={max}
          minLength={minLength}
          maxLength={maxLength}
          placeholder={placeholder}
          placeholderColor={placeholderColor}
          font={font}
          colorDefault={colorDefault}
          colorDisabled={colorDisabled}
          inputHeight={height}
          paddings={paddings}
          corners={corners}
          required={required}
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          invalid={invalid}
          borders={borders}
          borderColorDefault={borderColorDefault}
          borderColorHovered={borderColorHovered}
          borderColorPressed={borderColorPressed}
          borderColorInvalid={borderColorInvalid}
          backgroundColorDefault={backgroundColorDefault}
          backgroundColorHovered={backgroundColorHovered}
          backgroundColorPressed={backgroundColorPressed}
          icon={icon}
          actionButtonPosition={actionButtonPosition}
          caretColor={caretColor}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          secured={secured}
        />
        {iconName && (
          <ActionButton
            font={ButtonFonts.Button2}
            icon={iconName}
            iconSize={iconSize}
            colorDefault={actionButtonColorDefault}
            colorHovered={actionButtonColorHovered}
            colorPressed={actionButtonColorPressed}
            paddings={actionButtonPaddings}
            onClick={handleClick}
            tabIndex={-1}
          />
        )}
      </InputWrapper>
      {children}
    </InputLabel>
  )
}
